<script lang="ts">
  import { onMount } from 'svelte';
  import { isReportQueryWithinLimit, REPORT_QUERY_TOO_LARGE_MESSAGE } from './utils/report-query';
  import * as yup from 'yup';
  import { isEqual, capitalize } from 'lodash';

  import {
    DatePicker,
    DatePickerInput,
    MultiSelect,
    Button,
    DataTable,
    Toggle,
    InlineNotification,
    OverflowMenu,
    OverflowMenuItem,
  } from 'carbon-components-svelte';
  import Download16 from 'carbon-icons-svelte/lib/Download16';
  import FilterReset16 from 'carbon-icons-svelte/lib/FilterReset16';

  import {
    getOrganizations,
    getLicensesReport,
    getLicensesReportCsvUrl,
    getLicensesReportSearchParams,
    type LicensesReportRequest,
    type LicensesReportRow,
    type Organization,
    type PaginationParams,
  } from '../../services';
  import CollectionValueCell from '../../components/CollectionValueCell.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';

  import { getActualFeatures } from '../../services/licenses';
  import { noopSort, parseSortEvent } from '../../utils';

  type FormState = {
    organizations: string[];
    expirationDate: string | undefined;
    isPermanent: boolean | undefined;
    isActive: boolean;
    features: string[];
  };

  let pageData: { loading: boolean; stale?: boolean; error?: string } = { loading: true };
  let organizations: Organization[] = [];
  let features: { id: string; name: string }[] = [];

  let licenses: LicensesReportRow[] = [];
  let licensesCount = 0;

  const form: FormState = {
    organizations: [],
    expirationDate: undefined,
    isPermanent: undefined,
    isActive: true,
    features: [],
  };

  const pagination: PaginationParams = {
    orderBy: null,
    orderDirection: null,
    currentPage: 1,
    pageSize: 10,
  };

  const setFormFromUrlParams = () => {
    const url = new URL(window.location.href);
    form.organizations = url.searchParams.getAll('organization');
    form.expirationDate = url.searchParams.get('expirationDate') || undefined;
    form.isPermanent = url.searchParams.get('isPermanent') === 'true' || undefined;
    form.isActive = url.searchParams.get('isActive') !== 'false';
    form.features = url.searchParams.getAll('feature');
  };

  setFormFromUrlParams();

  const errors = {
    expirationDate: '',
  };

  const schema = yup.object().shape({
    organizations: yup.array().of(yup.string()),
    expirationDate: yup
      .string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^\d{4}-\d{2}-\d{2}$/, { message: 'Date must be in YYYY-MM-DD format', excludeEmptyString: true }),
    isPermanent: yup.boolean().nullable(),
    isActive: yup.boolean(),
    features: yup.array().of(yup.string()),
  });

  onMount(async () => {
    try {
      [organizations, features] = await Promise.all([getOrganizations(), getActualFeatures()]);
      pageData = { loading: false };
    } catch (error) {
      console.error('[LicensesReport] Failed to load organizations or features!', error);
      pageData = { loading: false, error: 'Failed to load organizations or features!' };
    }
  });

  const onPopstateChange = () => {
    setFormFromUrlParams();
    pageData.stale = true;
  };

  const onMultiSelectChange = (prop: keyof LicensesReportRequest, callback: (event: CustomEvent) => void) => (event: CustomEvent) => {
    if (!isEqual(form[prop], event.detail.selectedIds)) callback(event);
  };

  const onOrganizationChange = onMultiSelectChange('organizations', (event) => {
    form.organizations = event.detail.selectedIds as string[];
  });

  const onFeatureChange = onMultiSelectChange('features', (event) => {
    form.features = event.detail.selectedIds as string[];
  });

  const onExpirationDateChange = (event: CustomEvent) => {
    form.expirationDate = event.detail.dateStr || undefined;
    errors.expirationDate = '';
  };

  const onToggleIsPermanent = (event: CustomEvent) => {
    form.isPermanent = event.detail.toggled ? true : undefined;
    if (form.isPermanent) {
      form.expirationDate = undefined;
      errors.expirationDate = '';
    }
  };

  const onToggleIsActive = (event: CustomEvent) => {
    form.isActive = event.detail.toggled;
    if (form.isActive) {
      form.expirationDate = undefined;
      errors.expirationDate = '';
    }
  };

  const resetFilters = () => {
    form.organizations = [];
    form.expirationDate = undefined;
    form.isPermanent = undefined;
    form.isActive = true;
    form.features = [];

    errors.expirationDate = '';

    pageData.stale = false;
  };

  const validateForm = async () => {
    try {
      await schema.validate(form, { abortEarly: false });
      return true;
    } catch (error) {
      (error as yup.ValidationError).inner.forEach((e) => (errors[e.path as keyof typeof errors] = e.message));
      return false;
    }
  };

  const toRequest = (f: FormState): LicensesReportRequest => ({
    ...(f.organizations.length && { organizations: f.organizations }),
    ...(f.expirationDate !== undefined && { expirationDate: f.expirationDate }),
    ...(f.isPermanent !== undefined && { isPermanent: f.isPermanent }),
    isActive: f.isActive,
    ...(f.features.length && { features: f.features }),
  });

  const validateQuerySize = (...queries: URLSearchParams[]) => {
    if (queries.every(isReportQueryWithinLimit)) {
      if (pageData.error === REPORT_QUERY_TOO_LARGE_MESSAGE) {
        const { error, ...nextPageData } = pageData;
        pageData = nextPageData;
      }
      return true;
    }
    pageData = { loading: false, stale: true, error: REPORT_QUERY_TOO_LARGE_MESSAGE };
    return false;
  };

  const onDownloadReportClick = async () => {
    if (!(await validateForm())) return;

    const csvUrl = getLicensesReportCsvUrl(toRequest(form));
    if (!validateQuerySize(new URL(csvUrl).searchParams)) return;

    window.location.href = csvUrl;
  };

  const onGenerateReportClick = async () => {
    void generateReport({ resetPagination: true });
  };

  const onSortChange = (event: CustomEvent) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      pagination.orderBy = orderBy;
      pagination.orderDirection = orderDirection;
      void generateReport({ resetPagination: true });
    });

  const onPaginationChange = () => {
    void generateReport({ resetPagination: false });
  };

  const openInNewWindow = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const goToLicenseTemplate = (groupId: string, templateId: string) => () => {
    openInNewWindow(`/group/${groupId}?template=${templateId}#licenses`);
  };

  const goToGeneratedLicenses = (filename: string) => () => {
    const urlSearchParams = new URLSearchParams({ filter: filename });
    openInNewWindow(`/licenses?${urlSearchParams.toString()}#licenses`);
  };

  const generateReport = async ({ resetPagination }: { resetPagination: boolean }) => {
    if (!(await validateForm())) return;

    if (resetPagination) {
      pagination.currentPage = 1;
    }

    const requestQuery = getLicensesReportSearchParams(toRequest(form), pagination);
    const reportUrl = getUrlFromForm();
    if (!validateQuerySize(requestQuery, reportUrl.searchParams)) return;

    pageData = { loading: true };
    try {
      window.history.pushState({}, '', reportUrl);

      const { items, count } = await getLicensesReport(toRequest(form), pagination);
      licenses = items;
      licensesCount = count;

      pageData = { loading: false };
    } catch (error) {
      console.error('[LicensesReport] Failed to get licenses report!', error);
      pageData = { loading: false, stale: true, error: 'Failed to get licenses report!' };
    }
  };

  const getUrlFromForm = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pagination.currentPage.toString());
    url.searchParams.set('pageSize', pagination.pageSize.toString());

    url.searchParams.delete('organization');
    form.organizations.forEach((org) => url.searchParams.append('organization', org));

    if (form.expirationDate) {
      url.searchParams.set('expirationDate', form.expirationDate);
    } else {
      url.searchParams.delete('expirationDate');
    }

    if (form.isPermanent !== undefined) {
      url.searchParams.set('isPermanent', form.isPermanent.toString());
    } else {
      url.searchParams.delete('isPermanent');
    }

    if (form.isActive) {
      url.searchParams.delete('isActive');
    } else {
      url.searchParams.set('isActive', 'false');
    }

    url.searchParams.delete('feature');
    form.features?.forEach((feature) => url.searchParams.append('feature', feature));

    return url;
  };
</script>

{#if pageData.error}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle={pageData.error} />
{/if}

<LoadingSpinner loading={pageData.loading}>
  <div class="filters-reset-button">
    <Button on:click={resetFilters} iconDescription="Reset filters" icon={FilterReset16} kind="ghost">Reset filters</Button>
  </div>

  <div class="filters-row">
    <MultiSelect
      spellcheck="false"
      filterable
      labelText="Organization"
      placeholder="Select organizations..."
      selectedIds={form.organizations}
      on:select={onOrganizationChange}
      items={organizations.map(({ id, name }) => ({ id, text: name }))}
    />

    <MultiSelect
      spellcheck="false"
      filterable
      labelText="Features"
      placeholder="Select features..."
      selectedIds={form.features ?? []}
      on:select={onFeatureChange}
      items={features.map(({ name }) => ({ id: name, text: name }))}
    />
  </div>

  <div class="filters-row filters-row-margin">
    <div class="date-picker">
      {#key `${form.isPermanent}-${form.isActive}`}
        <DatePicker
          datePickerType="single"
          flatpickrProps={{ static: true }}
          on:change={onExpirationDateChange}
          value={form.isPermanent || form.isActive ? '' : form.expirationDate ?? ''}
          dateFormat="Y-m-d"
        >
          <DatePickerInput
            labelText="Expiration date"
            placeholder="yyyy-mm-dd"
            name="expirationDate"
            autocomplete="off"
            disabled={!!form.isPermanent || form.isActive}
            invalidText={errors.expirationDate}
            invalid={!!errors.expirationDate}
          />
        </DatePicker>
      {/key}
      <Toggle
        labelText="Permanent"
        toggled={!!form.isPermanent}
        on:toggle={onToggleIsPermanent}
        labelA=""
        labelB=""
        disabled={form.isActive}
      />
      <Toggle labelText="Currently active" toggled={form.isActive} on:toggle={onToggleIsActive} labelA="" labelB="" />
    </div>

    <div class="generate-report-buttons">
      <Button on:click={onDownloadReportClick} iconDescription="Download CSV report" icon={Download16} kind="tertiary" />
      <Button on:click={onGenerateReportClick}>Generate report</Button>
    </div>
  </div>

  {#if pageData.stale}
    <div class="stale-data-notification">
      <strong>Filters changed:</strong> These results reflect a previous query. Re-run the report to see current data.
    </div>
  {/if}

  <DataTable
    headers={[
      { key: 'licenseOwner.organization.name', value: 'Organization', sort: noopSort },
      { key: 'licenseOwner.name', value: 'Group', sort: noopSort },
      { key: 'filename', value: 'File Name', sort: noopSort },
      { key: 'expirationDate', value: 'Expiration Date', sort: noopSort },
      { key: 'cycle', value: 'Cycle', sort: noopSort, display: (value) => capitalize(value) },
      { key: 'features', value: 'Features', sort: false },
      { key: 'actions', empty: true, sort: false },
    ]}
    rows={licenses}
    sortable
    on:click:header={onSortChange}
  >
    <svelte:fragment slot="cell" let:cell let:row>
      {#if cell.key === 'features'}
        <CollectionValueCell value={cell.value} hideLabel />
      {:else if cell.key === 'actions'}
        <OverflowMenu aria-label="Open and close list of options" flipped portalMenu>
          <OverflowMenuItem text="Go to Template" on:click={goToLicenseTemplate(row.ownerId, row.templateId)} />
          <OverflowMenuItem text="Go to Generated" on:click={goToGeneratedLicenses(row.filename)} />
        </OverflowMenu>
      {:else if cell.key === 'filename'}
        <div class="file-name-cell">{cell.value}<span class="file-extension-cell">.lic</span></div>
      {:else if cell.key === 'expirationDate'}
        {cell.value ?? 'Permanent'}
      {:else if cell.display}
        {cell.display(cell.value, row)}
      {:else}
        {cell.value}
      {/if}
    </svelte:fragment>
  </DataTable>
  {#if licenses.length === 0 && !pageData.stale}
    <div class="empty-state">No licenses found for the selected filters.</div>
  {/if}
  <PaginationWithRouting
    bind:page={pagination.currentPage}
    bind:pageSize={pagination.pageSize}
    pageSizes={[10, 25, 50]}
    totalItems={licensesCount}
    on:change={onPaginationChange}
  />
</LoadingSpinner>

<svelte:window on:popstate={onPopstateChange} />

<style>
  .filters-reset-button {
    display: flex;
    justify-content: flex-end;
  }

  @media (min-width: 42rem) {
    .filters-reset-button {
      margin-top: -2rem;
      right: 0px;
      position: absolute;
    }
  }

  .filters-row {
    display: flex;
    gap: 1em;
    flex-wrap: wrap;
  }

  .filters-row-margin {
    margin-top: 1em;
    margin-bottom: 2em;
  }

  .filters-row :global(.bx--multi-select__wrapper) {
    flex: 1 1 350px;
    min-width: calc(50% - 0.5em);
  }

  @media (min-width: 790px) {
    .filters-row :global(.bx--multi-select__wrapper) {
      max-width: calc(50% - 0.5em);
    }
  }

  .date-picker {
    display: flex;
    gap: 1em;
    flex-wrap: wrap;
  }

  .date-picker :global(.bx--date-picker) {
    align-items: start;
  }

  .generate-report-buttons {
    align-content: end;
    flex: auto;
    text-align: end;
  }

  .stale-data-notification {
    align-content: center;
    color: var(--cds-text-secondary);
    text-align: center;
  }

  .empty-state {
    align-content: center;
    background-color: var(--cds-layer);
    color: var(--cds-text-secondary);
    height: var(--cds-spacing-09);
    text-align: center;
  }

  .file-name-cell {
    word-break: break-all;
  }

  .file-extension-cell {
    opacity: 0.6;
    font-size: 0.9em;
    user-select: none;
  }
</style>
